import type { KeyboardEvent, MouseEvent, ReactNode } from "react";

import type { TaskItem } from "@domain/task-state";

import { TaskCheck } from "./TaskCheck";
import { relativeTime, TYPE_PRESENTATION } from "./typePresentation";

export interface TaskListRowProps {
  item: TaskItem;
  /** Injected for deterministic relative-time rendering; defaults to Date.now(). */
  now?: number;
  /** Optional trailing slot (kept from the mockup's sources/pill affordance). */
  trailing?: ReactNode;
  /**
   * When set, the row is a navigation affordance to the task's detail page
   * (`role="link"` — navigation, not an action button; the route layer owns
   * the actual navigate call so this component stays router-free).
   */
  onOpen?: () => void;
  /**
   * Relay a complete intent for this row (praxis-intent-relay §3.7).
   * Threaded into the check; the check click never triggers `onOpen`.
   */
  onComplete?: () => void;
  /** A relayed write for this row is pending — the check pulses sky. */
  pendingWrite?: boolean;
  /** Inline relay error copy (failed / unconfirmed) — no page banner. */
  relayError?: string;
}

/** One task row: self-completing check + type glyph + title + peek + meta. */
export function TaskListRow({
  item,
  now,
  trailing,
  onOpen,
  onComplete,
  pendingWrite,
  relayError,
}: TaskListRowProps) {
  const { icon: Icon, tone } = TYPE_PRESENTATION[item.type];
  const nowMs = now ?? Date.now();

  const interactive =
    onOpen !== undefined
      ? {
          role: "link" as const,
          tabIndex: 0,
          onClick: onOpen,
          onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
            // Keyboard nav fires only from the row itself — Enter/Space
            // inside the check button (or any future child control) must
            // not double-fire navigation.
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          },
        }
      : {};

  const handleComplete =
    onComplete !== undefined
      ? {
          onComplete,
          taskTitle: item.title,
        }
      : {};

  return (
    <div
      {...interactive}
      className={`flex items-center gap-3 rounded-2xl border border-home-ink/8 dark:border-white/8 bg-white dark:bg-surface-dark-card px-4 py-3.5 shadow-[0_10px_26px_-22px_rgba(33,31,28,0.4)] ${
        onOpen !== undefined
          ? "cursor-pointer transition-colors hover:border-home-ink/20 dark:hover:border-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-home-ink/40 dark:focus-visible:outline-white/40"
          : ""
      }`}
    >
      {/* The check is an action, the row is a link — the click must not
          bubble into navigation. */}
      <span
        onClick={(e: MouseEvent<HTMLSpanElement>) => {
          if (onComplete !== undefined) e.stopPropagation();
        }}
        className="flex shrink-0 items-center"
      >
        <TaskCheck
          state={item.displayState}
          pending={pendingWrite}
          {...handleComplete}
        />
      </span>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${tone}`}
      >
        <Icon className="h-4 w-4" strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-body text-[13.5px] font-semibold text-home-ink/85 dark:text-white/85">
          {item.title}
        </p>
        {relayError !== undefined ? (
          <p className="truncate font-body text-[12px] text-home-terracotta dark:text-home-terracotta">
            {relayError}
          </p>
        ) : item.description ? (
          <p className="truncate font-body text-[12.5px] text-home-ink/55 dark:text-white/55">
            {item.description}
          </p>
        ) : null}
      </div>
      {trailing ?? null}
      <span className="shrink-0 font-body text-[11px] text-home-ink/40 dark:text-white/40">
        {relativeTime(item.updatedAt, nowMs)}
      </span>
    </div>
  );
}
